import { useState } from "react";
import {
  CanAccess,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
} from "ra-core";
import { Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import type {
  FeatureCohort,
  FeatureCohortImplication,
  FeatureCohortMember,
} from "../types";
import { AddMemberDialog } from "./AddMemberDialog";
import { displayPhone } from "./normalizePhone";
import { impliedMemberPhones } from "./resolve";

/**
 * Feature Cohorts — phone-number roles / feature flags for the WhatsApp bot.
 *
 * A cohort (Admins, Real Estate, ...) is simultaneously a role and a feature
 * flag: `admin` gates the bot's debug menu, `real_estate` gates the 🏠 Real
 * Estate occasion. Members are WhatsApp numbers (wa_ids). Removing a member
 * disables the feature for them within the bot's ~60s cache.
 *
 * Admin-only (same CanAccess rule as the Users menu). Not to be confused with
 * the WhatsApp *broadcast audience* cohorts — those are engagement filters.
 */
export const FeatureCohortsPage = () => (
  <CanAccess resource="feature_cohorts" action="list">
    <FeatureCohortsView />
  </CanAccess>
);

const FeatureCohortsView = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const {
    data: cohorts,
    error: cohortsError,
    isPending: cohortsPending,
  } = useGetList<FeatureCohort>("feature_cohorts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "slug", order: "ASC" },
  });
  const { data: implications } = useGetList<FeatureCohortImplication>(
    "feature_cohort_implications",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: members } = useGetList<FeatureCohortMember>(
    "feature_cohort_members",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const [deleteOne] = useDelete();

  if (cohortsError) {
    return <SetupNotice />;
  }
  if (cohortsPending) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Loading feature cohorts…
      </div>
    );
  }

  const cohortList = cohorts ?? [];
  const selected =
    cohortList.find((cohort) => cohort.slug === selectedSlug) ?? cohortList[0];
  const allRows = members ?? [];
  const allImplications = implications ?? [];

  const rowsFor = (slug: string) =>
    allRows.filter((row) => row.cohort_slug === slug);
  const grantCount = (slug: string) =>
    rowsFor(slug).filter((row) => row.mode === "grant").length;

  const grants = selected
    ? rowsFor(selected.slug).filter((row) => row.mode === "grant")
    : [];
  const denies = selected
    ? rowsFor(selected.slug).filter((row) => row.mode === "deny")
    : [];
  // Phones effective only via a parent (e.g. Admins -> Real Estate), in either
  // environment. Display-only — manage them in the parent cohort.
  const impliedPhones = selected
    ? [
        ...new Set([
          ...impliedMemberPhones(
            selected.slug,
            allRows,
            allImplications,
            "staging",
          ),
          ...impliedMemberPhones(
            selected.slug,
            allRows,
            allImplications,
            "prod",
          ),
        ]),
      ]
    : [];
  const impliedParents = selected
    ? allImplications
        .filter((implication) => implication.child_slug === selected.slug)
        .map(
          (implication) =>
            cohortList.find((cohort) => cohort.slug === implication.parent_slug)
              ?.name ?? implication.parent_slug,
        )
    : [];

  const removeRow = (row: FeatureCohortMember, confirmText: string) => {
    if (!window.confirm(confirmText)) {
      return;
    }
    deleteOne(
      "feature_cohort_members",
      { id: row.id, previousData: row },
      {
        onSuccess: () => {
          notify("Removed — the bot picks this up within a minute.", {
            type: "info",
          });
          refresh();
        },
        onError: () =>
          notify("Could not remove the row — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div>
        <h1 className="text-xl font-semibold">Feature Cohorts</h1>
        <p className="text-sm text-muted-foreground">
          Phone-number roles for the WhatsApp bot. Adding a number enables the
          cohort&apos;s features for it (bot cache: ~1 minute); removing it
          disables them. Resolution: deny overrides &gt; grants &gt; implied via
          Admins.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {cohortList.map((cohort) => {
          const isSelected = selected?.slug === cohort.slug;
          const implies = allImplications
            .filter((implication) => implication.parent_slug === cohort.slug)
            .map(
              (implication) =>
                cohortList.find((c) => c.slug === implication.child_slug)
                  ?.name ?? implication.child_slug,
            );
          return (
            <Card
              key={cohort.slug}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedSlug(cohort.slug)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setSelectedSlug(cohort.slug);
                }
              }}
              className={`w-64 cursor-pointer transition-colors ${
                isSelected
                  ? "border-primary"
                  : "hover:border-muted-foreground/40"
              }`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  {cohort.name}
                  <Badge variant="secondary">{grantCount(cohort.slug)}</Badge>
                </CardTitle>
                <CardDescription className="line-clamp-2 text-xs">
                  {cohort.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1 pt-0">
                {!cohort.self_service && (
                  <Badge variant="outline" className="text-xs">
                    CRM-managed only
                  </Badge>
                )}
                {implies.map((name) => (
                  <Badge key={name} variant="outline" className="text-xs">
                    implies {name}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          );
        })}
        {cohortList.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No cohorts found — run the feature_cohorts migration (it seeds
            Admins and Real Estate).
          </p>
        )}
      </div>

      {selected && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">
                {selected.name} members
              </CardTitle>
              <CardDescription>{selected.description}</CardDescription>
            </div>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add member
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <section>
              {grants.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No direct members yet.
                </p>
              ) : (
                <ul className="flex flex-col">
                  {grants.map((row) => (
                    <MemberRow
                      key={row.id}
                      row={row}
                      onRemove={() =>
                        removeRow(
                          row,
                          `Remove ${displayPhone(row.phone_digits)} from ${selected.name}? The feature turns off for them within a minute.`,
                        )
                      }
                    />
                  ))}
                </ul>
              )}
            </section>

            {impliedPhones.length > 0 && (
              <section>
                <Separator className="mb-3" />
                <h3 className="mb-1 text-sm font-medium">
                  Implied via {impliedParents.join(", ") || "parent cohorts"}
                </h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  These numbers get {selected.name} automatically. To exclude
                  one, remove it from the parent cohort — or let the user turn
                  it off for themselves from the bot&apos;s debug menu.
                </p>
                <ul className="flex flex-wrap gap-2">
                  {impliedPhones.map((phone) => (
                    <Badge key={phone} variant="secondary">
                      {displayPhone(phone)}
                    </Badge>
                  ))}
                </ul>
              </section>
            )}

            {denies.length > 0 && (
              <section>
                <Separator className="mb-3" />
                <h3 className="mb-1 text-sm font-medium">Disabled overrides</h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  Deny rows beat grants and implications. Self-service rows are
                  written from the bot&apos;s debug menu; removing one
                  re-enables the flag.
                </p>
                <ul className="flex flex-col">
                  {denies.map((row) => (
                    <MemberRow
                      key={row.id}
                      row={row}
                      deny
                      onRemove={() =>
                        removeRow(
                          row,
                          `Remove the disabled override for ${displayPhone(row.phone_digits)}? ${selected.name} turns back on for them.`,
                        )
                      }
                    />
                  ))}
                </ul>
              </section>
            )}
          </CardContent>
        </Card>
      )}

      {selected && (
        <AddMemberDialog
          cohort={selected}
          open={addOpen}
          onOpenChange={setAddOpen}
          onCreated={() => refresh()}
        />
      )}
    </div>
  );
};

const MemberRow = ({
  row,
  deny = false,
  onRemove,
}: {
  row: FeatureCohortMember;
  deny?: boolean;
  onRemove: () => void;
}) => (
  <li className="flex items-center gap-2 border-b py-2 text-sm last:border-b-0">
    <span className="font-mono">{displayPhone(row.phone_digits)}</span>
    {row.note && (
      <span className="truncate text-muted-foreground">{row.note}</span>
    )}
    <span className="ml-auto flex items-center gap-1">
      {deny && <Badge variant="destructive">disabled</Badge>}
      {row.environment !== "all" && (
        <Badge variant="outline">{row.environment}</Badge>
      )}
      {row.source !== "crm" && <Badge variant="outline">{row.source}</Badge>}
      {row.created_at && (
        <span className="text-xs text-muted-foreground">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Remove"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </span>
  </li>
);

const SetupNotice = () => (
  <div className="p-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Feature Cohorts is not provisioned yet
        </CardTitle>
        <CardDescription>
          The feature_cohorts tables were not found in Supabase. Apply the
          migration{" "}
          <code className="font-mono">
            supabase/migrations/20260610150000_feature_cohorts.sql
          </code>{" "}
          to this project (SQL editor or <code>supabase db push</code>), then
          reload. The deploy workflow cannot apply it automatically while the
          repo has no Supabase secrets.
        </CardDescription>
      </CardHeader>
    </Card>
  </div>
);
